import { useCallback, useEffect, useRef, useState } from 'react';
import { vintedStatus, vintedLogin, vintedLogout, vintedUebung, vintedLog, abgleich as abgleichRufen, offeneVintedPakete, isAnzeigeOn, setAnzeigeOn, isAusgabeOn, setAusgabeOn, isAnnahmeOn, setAnnahmeOn, vintedCode, notiz, vintedUebersicht, rueckgabeMitFach, vintedFristen, fristenMitPaket, vt } from '../services/vinted';
import { planUpdates, formatCode } from '../services/vintedNames';

/**
 * 🧪 Einstellungen → Vinted-Anbindung (nur freigeschaltete Konten).
 * Zustand und Anmeldung bei Vinted Go (Passwort nur zum Anmelden, danach verlängert sich das Token von selbst),
 * Ein/Aus auf diesem Gerät, Live-Schalter, Abgleich PaketPilot ↔ Vinted, Übungs-Ausgabe und das Protokoll.
 *
 * Nach dem Abgleich: Pakete, die in Vinted nicht mehr im Bestand stehen (dort ausgegeben), werden NICHT von
 * selbst ausgegeben — es kommt eine Rückfrage („Möchtest du das übernehmen?"). Pakete der letzten 12 Stunden
 * bleiben außen vor: sie könnten in Vinted schlicht noch nicht gescannt sein.
 */
const FRISCH_MS = 12 * 60 * 60 * 1000;
const MAX_ZEILEN = 40; // je Abweichungsliste (bei großem Bestand können es Hunderte sein)

export default function VintedPanel({ colors, language, pakete, onNamen, onAbgeholt, onAnlegen, onToggle, onClose }) {
  const T = vt(language);
  const dark = colors?.darkMode;
  const f = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff', zeile: dark ? '#2d333b' : '#f5f6f8' };

  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [meldetAn, setMeldetAn] = useState(false);
  const [loginMeldung, setLoginMeldung] = useState(null); // {ok, text}
  const [an, setAn] = useState(() => isAnzeigeOn());
  const [ausgabeAn, setAusgabeAnState] = useState(() => isAusgabeOn());
  const [annahmeAn, setAnnahmeAnState] = useState(() => isAnnahmeOn());
  const [liveFrage, setLiveFrage] = useState(null); // 'annahme' | 'ausgabe' — Rückfrage vor dem Einschalten
  const [liveAusFrage, setLiveAusFrage] = useState(null); // 'annahme' | 'ausgabe' — Rückfrage vor dem Ausschalten (Schutz gegen versehentliches Scroll-Tippen)
  const [log, setLog] = useState(null);
  const [offen, setOffen] = useState(null); // aufgeklappter Protokoll-Eintrag
  const [abgleich, setAbgleich] = useState(null); // null | {laeuft} | Ergebnis
  const [uebung, setUebung] = useState(null);     // null | {laeuft} | Ergebnis der Übungs-Ausgabe
  const [namenGesetzt, setNamenGesetzt] = useState(null);
  const [popupZu, setPopupZu] = useState(false); // Rückfrage „in Vinted ausgegeben" mit „Nicht jetzt" geschlossen
  const [uebernimmt, setUebernimmt] = useState(false);
  const [uebernommen, setUebernommen] = useState(null);
  const [erledigt, setErledigt] = useState({});      // 'add:123456' → true (Zeile abgearbeitet)
  const [laeuftAktion, setLaeuftAktion] = useState(null);
  const [nameFuer, setNameFuer] = useState({});      // Kurznummer → angepasster Name (nur „Hinzufügen")
  const [fachFuer, setFachFuer] = useState({});      // Kurznummer → Fach (nur „Hinzufügen")
  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; }, []);

  const [lage, setLage] = useState(null); // Übersicht: Zähler, Rückgabe-Liste, Posteingang
  const rueckListe = rueckgabeMitFach(lage && lage.ok ? lage.rueckgabe : [], pakete);
  const ladeStatus = useCallback(async () => {
    setStatus(null);
    const s = await vintedStatus();
    if (!lebt.current) return;
    setStatus(s);
    if (s.angemeldet) { const u = await vintedUebersicht(); if (lebt.current) setLage(u); }
  }, []);
  const ladeLog = useCallback(async () => { const r = await vintedLog(100); if (lebt.current) setLog(r.entries || []); }, []);
  // Sendungsverfolgung: kommt aus der Datenbank (der Server fragt die Vinted-Webseite im Hintergrund ab) — unabhängig von der Anmeldung
  const [fristen, setFristen] = useState(null);
  const [fristPopupZu, setFristPopupZu] = useState(false);
  const [fristUebernommen, setFristUebernommen] = useState(null);
  const ladeFristen = useCallback(async () => { const r = await vintedFristen(); if (lebt.current && r.ok) setFristen(r.pakete || []); }, []);
  useEffect(() => { ladeStatus(); ladeLog(); ladeFristen(); }, [ladeStatus, ladeLog, ladeFristen]);
  useEffect(() => {
    const sync = () => { setAn(isAnzeigeOn()); setAnnahmeAnState(isAnnahmeOn()); setAusgabeAnState(isAusgabeOn()); };
    window.addEventListener('kiosk:profile-applied', sync);
    return () => window.removeEventListener('kiosk:profile-applied', sync);
  }, []);
  const frist = fristenMitPaket(fristen, pakete);

  const statusText = () => {
    if (!status) return T('laedt');
    if (status.reason === 'nicht_freigeschaltet') return '⛔ ' + T('stGesperrt');
    if (status.reason === 'offline') return '⚠️ ' + T('stOffline');
    if (status.konfiguriert === false) return '🟠 ' + T('stKonfig');
    if (!status.angemeldet) return '🟡 ' + T('stLogin');
    if (status.letzterFehler) return '🟠 ' + T('stFehler');
    return '🟢 ' + T('stOk');
  };
  const angemeldet = !!(status && status.angemeldet);
  // Anmeldefeld: nicht angemeldet, Token verlängert sich nicht von selbst oder die letzte Erneuerung ist schiefgegangen
  const anmeldenZeigen = !!(status && !status.reason && status.konfiguriert !== false && (!angemeldet || !status.erneuerbar || status.letzterFehler));

  const anmelden = async () => {
    setMeldetAn(true); setLoginMeldung(null);
    const r = await vintedLogin(email.trim(), passwort);
    if (!lebt.current) return;
    setMeldetAn(false);
    setPasswort('');
    setLoginMeldung(r.ok ? { ok: true, text: T('loginOk') } : { ok: false, text: `${T('loginFehler')}${r.message ? ': ' + r.message : ''}` });
    ladeStatus(); ladeLog();
  };

  const abmelden = async () => { await vintedLogout(); setLage(null); setAbgleich(null); setUebung(null); ladeStatus(); ladeLog(); };
  const uebungTesten = async () => {
    setUebung({ laeuft: true });
    const r = await vintedUebung();
    if (!lebt.current) return;
    setUebung(r); ladeLog();
  };

  const schalten = (on) => { setAnzeigeOn(on); setAn(on); if (onToggle) onToggle(on); };
  // Live-Schalter (buchen echt in Vinted): im Konto-Profil gesichert, Ein/Aus nur nach Rückfrage, jede Änderung steht im Protokoll
  const liveSchalten = (art, on) => {
    if (art === 'annahme') { setAnnahmeOn(on); setAnnahmeAnState(on); } else { setAusgabeOn(on); setAusgabeAnState(on); }
    notiz('live_schalter', '', { ok: true, message: `${art === 'annahme' ? 'Annahme' : 'Ausgabe'} live buchen: ${on ? 'EIN (ausdrücklich bestätigt)' : 'AUS'}` }).then(ladeLog);
    if (onToggle) onToggle(on);
  };

  // In Vinted ausgegeben? = offen in PaketPilot, aber laut Abgleich nicht mehr im Vinted-Bestand
  const abgeholt = (() => {
    if (!abgleich || !abgleich.done || !abgleich.ok) return { alt: [], frisch: 0 };
    const codes = new Set((abgleich.nurPaketPilot || []).map((p) => p.code));
    const grenze = Date.now() - FRISCH_MS;
    const alt = [];
    let frisch = 0;
    for (const p of pakete || []) {
      if (!p || p.deleted || p.ausgegebenAm || p.zurueckgeschickt || !codes.has(vintedCode(p))) continue;
      if (new Date(p.datum || 0).getTime() > grenze) frisch++; else alt.push(p);
    }
    return { alt, frisch };
  })();

  const uebernehmen = async () => {
    setUebernimmt(true);
    const n = await onAbgeholt(abgeholt.alt.map((p) => p.id));
    notiz('uebernahme', '', { ok: true, message: `${n} Pakete nach dem Abgleich als ausgegeben übernommen` });
    if (!lebt.current) return;
    setUebernimmt(false); setUebernommen(n); setPopupZu(true); ladeLog();
  };

  // Laut Sendungsverfolgung abgeholt → nach Rückfrage in PaketPilot als ausgegeben übernehmen (nie von selbst)
  const fristUebernehmen = async () => {
    setUebernimmt(true);
    const n = await onAbgeholt(frist.abgeholt.map((z) => z.id));
    notiz('uebernahme', '', { ok: true, message: `${n} Pakete laut Sendungsverfolgung als ausgegeben übernommen` });
    if (!lebt.current) return;
    setUebernimmt(false); setFristUebernommen(n); setFristPopupZu(true); ladeLog();
  };
  // Einzeln austragen: laut Verfolgung nicht mehr abholbereit (z. B. auf dem Rückweg zum Absender). z.code = Kurznummer
  const fristAustragen = (z) => tu(`weg:${z.code}`, async () => {
    await onAbgeholt([z.id]);
    notiz('uebernahme', z.voll, { ok: true, message: `1 Paket laut Sendungsverfolgung ausgetragen (${z.status})`.slice(0, 200) });
  });

  // ── Abweichungen einzeln bearbeiten ──
  const meinPaket = (c) => (pakete || []).find((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && vintedCode(p) === c);
  const tu = async (key, fn) => {
    setLaeuftAktion(key);
    try { await fn(); if (lebt.current) setErledigt((e) => ({ ...e, [key]: true })); } finally { if (lebt.current) setLaeuftAktion(null); }
  };
  // Nur in Vinted → in PaketPilot anlegen. Vom Bestand kennen wir nur die Kurznummer → Sendungsnummer „VGS" + Kurznummer
  // (wie beim Screenshot-Import); Name und Fach lassen sich vorher anpassen.
  const hinzufuegen = (p) => tu(`add:${p.code}`, async () => {
    await onAnlegen({ tracking: `VGS${p.code}`, name: (nameFuer[p.code] ?? p.name).trim() || p.name, regalplatz: (fachFuer[p.code] || '').trim() });
    notiz('annahme_paketpilot', `VGS${p.code}`, { ok: true, message: 'aus dem Abgleich hinzugefügt' });
  });
  // Nur in PaketPilot → dort austragen (als ausgegeben markieren)
  const austragen = (p) => tu(`aus:${p.code}`, async () => {
    const m = meinPaket(p.code);
    if (!m) return;
    await onAbgeholt([m.id]);
    notiz('uebernahme', p.code, { ok: true, message: '1 Paket aus dem Abgleich ausgetragen' });
  });
  const nameNehmen = (p) => tu(`name:${p.code}`, async () => {
    const m = meinPaket(p.code);
    if (m) await onNamen([{ paket: m, code: p.code, name: p.vinted }]);
  });

  const starteAbgleich = async () => {
    setNamenGesetzt(null); setPopupZu(false); setUebernommen(null); setErledigt({}); setNameFuer({}); setFachFuer({});
    setAbgleich({ laeuft: true });
    const r = await abgleichRufen(offeneVintedPakete(pakete));
    if (!lebt.current) return;
    setAbgleich(r.ok ? r : { ok: false, message: r.message || r.reason });
    ladeLog();
  };

  const namenSetzen = async () => {
    const updates = planUpdates(pakete, abgleich.nameFehlt || []);
    const n = updates.length ? await onNamen(updates) : 0;
    if (lebt.current) setNamenGesetzt(n);
  };

  const feld = { width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${f.rand}`, fontSize: 15, background: f.feld, color: f.text, boxSizing: 'border-box', marginTop: 8 };
  const knopf = (primary) => ({ width: '100%', padding: '12px', borderRadius: 10, marginTop: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', border: primary ? 'none' : `1px solid ${f.rand}`, background: primary ? '#007782' : 'transparent', color: primary ? '#fff' : f.text });
  const klein = { padding: '6px 10px', borderRadius: 8, border: `1px solid ${f.rand}`, background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer' };
  const kopf = (txt, rechts) => (<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, marginBottom: 4 }}><b style={{ fontSize: 15 }}>{txt}</b>{rechts}</div>);
  const hinweis = (txt) => <div style={{ fontSize: 13, color: f.sub, marginTop: 6, lineHeight: 1.5 }}>{txt}</div>;
  const zeit = (iso) => { const d = new Date(iso); return `${d.toLocaleDateString(language, { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`; };
  const code = (c) => (/^\d{6}$/.test(c) ? formatCode(c) : c);
  const miniFeld = { padding: '6px 8px', borderRadius: 6, border: `1px solid ${f.rand}`, fontSize: 13, background: f.feld, color: f.text, boxSizing: 'border-box', minWidth: 0 };
  /** Liste von Abweichungen mit einer Aktion je Zeile (höchstens MAX_ZEILEN anzeigen, der Rest als Zahl). */
  const aktionen = (titel, art, eintraege, inhalt, knopfText, aktion) => (eintraege && eintraege.length ? (
    <div data-testid={`vb-liste-${art}`} style={{ marginTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{eintraege.length} × {titel}</div>
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {eintraege.slice(0, MAX_ZEILEN).map((p) => {
          const key = `${art}:${p.code}`;
          return (
            <div key={p.code} data-testid={`vb-zeile-${art}-${p.code}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 13, padding: '5px 0', borderTop: `1px solid ${f.rand}` }}>
              <b style={{ whiteSpace: 'nowrap' }}>{code(p.code)}</b>
              {erledigt[key]
                ? <span style={{ flex: 1, color: '#2e7d32' }}>✓ {T('erledigt')}</span>
                : <>{inhalt(p)}<button type="button" data-testid={`vb-tu-${art}-${p.code}`} disabled={laeuftAktion !== null} onClick={() => aktion(p)} style={{ ...klein, opacity: laeuftAktion !== null ? 0.5 : 1 }}>{laeuftAktion === key ? '⏳' : knopfText}</button></>}
            </div>
          );
        })}
      </div>
      {eintraege.length > MAX_ZEILEN && <div style={{ fontSize: 12, color: f.sub, marginTop: 4 }}>{T('weitere', { n: eintraege.length - MAX_ZEILEN })}</div>}
    </div>
  ) : null);
  const liste = (titel, eintraege, zeile) => (eintraege && eintraege.length ? (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{eintraege.length} × {titel}</div>
      <div style={{ fontSize: 13, color: f.sub, lineHeight: 1.6, maxHeight: 140, overflowY: 'auto' }}>{eintraege.map((p) => <div key={p.code}>{zeile(p)}</div>)}</div>
    </div>
  ) : null);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="vinted-panel" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <b style={{ fontSize: 18 }}>🧪 {T('titel')}</b>
          <button type="button" onClick={onClose} style={klein}>{T('schliessen')}</button>
        </div>
        {hinweis(T('beschreibung'))}

        {kopf(T('status'), <button type="button" data-testid="vb-status-refresh" onClick={ladeStatus} style={klein}>{T('pruefen')}</button>)}
        <div style={{ padding: '10px 12px', borderRadius: 8, background: f.zeile, fontSize: 14, lineHeight: 1.6 }}>
          <div data-testid="vb-status">{statusText()}</div>
          {angemeldet && (
            <div style={{ fontSize: 13, color: f.sub }}>
              {status.emailHinweis ? `${T('konto')}: ${status.emailHinweis} · ` : ''}{status.angemeldetSeit ? `${T('seit')} ${zeit(status.angemeldetSeit)} · ` : ''}{status.erneuerbar ? T('erneuerbar') : T('nichtErneuerbar')}
            </div>
          )}
          {status && status.letzterFehler && <div style={{ fontSize: 13, color: '#c62828' }}>{T('letzterFehler')}{status.letzterFehlerAt ? ` (${zeit(status.letzterFehlerAt)})` : ''}: {status.letzterFehler}</div>}
        </div>

        {/* Was heute bei Vinted ansteht (nur gelesen): Zähler, Posteingang, abgelaufene Pakete mit PaketPilot-Fach */}
        {lage && lage.ok && (
          <div data-testid="vb-heute">
            {kopf(T('heute'), lage.alter > 60 ? <span style={{ fontSize: 12, color: f.sub }}>{T('standVon', { m: Math.round(lage.alter / 60) })}</span> : null)}
            <div style={{ padding: '10px 12px', borderRadius: 8, background: f.zeile, fontSize: 14, lineHeight: 1.7 }}>
              <div>📦 {T('hKunden', { n: lage.kunden ?? '–' })}{lage.kundenZuScannen > 0 && <b style={{ color: '#c62828' }}> · {T('hZuScannen', { n: lage.kundenZuScannen })}</b>}</div>
              <div>🚚 {T('hRueck', { n: lage.zuScannen ?? 0 })} · {T('hSack', { n: lage.nichtImSack ?? '–' })}</div>
              <div>✉️ {lage.posteingang && lage.posteingang.handlung ? <b style={{ color: '#c62828' }}>{T('hPost')}</b> : T('hPostLeer')}</div>
            </div>
            {rueckListe.length > 0 && (
              <div data-testid="vb-rueckgabe" style={{ marginTop: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{rueckListe.length} × {T('rueckTitel')}</div>
                <div style={{ fontSize: 12, color: f.sub, marginTop: 2 }}>{T('rueckHinweis')}</div>
                <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 4 }}>
                  {rueckListe.slice(0, MAX_ZEILEN).map((r) => (
                    <div key={r.code} data-testid={`vb-rueck-${r.code}`} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '5px 0', borderTop: `1px solid ${f.rand}` }}>
                      <b style={{ minWidth: 52 }}>{r.bekannt ? `📍 ${r.fach || '—'}` : '⚠️'}</b>
                      <span style={{ whiteSpace: 'nowrap' }}>{code(r.code)}</span>
                      <span style={{ flex: 1 }}>{r.name}{!r.bekannt ? ` · ${T('nichtInPP')}` : ''}</span>
                    </div>
                  ))}
                </div>
                {rueckListe.length > MAX_ZEILEN && <div style={{ fontSize: 12, color: f.sub, marginTop: 4 }}>{T('weitere', { n: rueckListe.length - MAX_ZEILEN })}</div>}
              </div>
            )}
          </div>
        )}

        {/* Sendungsverfolgung (öffentliche Vinted-Webseite): Abholfristen und was laut Vinted nicht mehr abholbereit ist */}
        {fristen && (
          <div data-testid="vb-fristen">
            {kopf(T('fristTitel'))}
            <div style={{ padding: '10px 12px', borderRadius: 8, background: f.zeile, fontSize: 14, lineHeight: 1.7 }}>
              {frist.gesamt === 0
                ? <div>{T('fristLeer')}</div>
                : <div>🔎 {T('fristZahl', { g: frist.gesamt, b: frist.bereit })}{frist.ablauf.length > 0 && <b style={{ color: '#c62828' }}> · {T('fristAblaufZahl', { n: frist.ablauf.length })}</b>}</div>}
              {frist.ohneNummer > 0 && <div style={{ fontSize: 12, color: f.sub }}>{T('fristOhne', { n: frist.ohneNummer })}</div>}
            </div>
            {hinweis(T('fristHinweis'))}
            {frist.ablauf.length > 0 && (
              <div data-testid="vb-frist-ablauf" style={{ marginTop: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{frist.ablauf.length} × {T('fristAblauf')}</div>
                <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 4 }}>
                  {frist.ablauf.slice(0, MAX_ZEILEN).map((z) => (
                    <div key={z.code} data-testid={`vb-frist-${z.kurz}`} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '5px 0', borderTop: `1px solid ${f.rand}` }}>
                      <b style={{ minWidth: 52 }}>📍 {z.fach || '—'}</b>
                      <span style={{ whiteSpace: 'nowrap' }}>{code(z.kurz)}</span>
                      <span style={{ flex: 1 }}>{z.name}</span>
                      <b style={{ whiteSpace: 'nowrap', color: '#c62828' }}>{z.restTage <= 0 ? T('fristHeute') : T('fristTage', { n: z.restTage })}</b>
                    </div>
                  ))}
                </div>
                {frist.ablauf.length > MAX_ZEILEN && <div style={{ fontSize: 12, color: f.sub, marginTop: 4 }}>{T('weitere', { n: frist.ablauf.length - MAX_ZEILEN })}</div>}
              </div>
            )}
            {aktionen(T('fristAnders'), 'weg', frist.anders.map((z) => ({ ...z, code: z.kurz, voll: z.code })),
              (z) => <span style={{ flex: '1 1 140px' }}>{z.fach ? `📍 ${z.fach} · ` : ''}{z.name} · <i>{z.status}</i></span>, T('austragen'), fristAustragen)}
            {fristUebernommen !== null && <div data-testid="vb-frist-uebernommen" style={{ marginTop: 8, fontSize: 14, color: '#2e7d32' }}>✓ {T('uebernommen', { n: fristUebernommen })}</div>}
            {frist.abgeholt.length > 0 && fristPopupZu && (
              <button type="button" data-testid="vb-frist-popup-wieder" onClick={() => setFristPopupZu(false)} style={knopf(false)}>{T('fristPopupTitel')} ({frist.abgeholt.length})</button>
            )}
          </div>
        )}

        {/* Rückfrage: laut Sendungsverfolgung abgeholt → in PaketPilot übernehmen? (nie von selbst) */}
        {frist.abgeholt.length > 0 && !fristPopupZu && !(abgeholt.alt.length > 0 && !popupZu) && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 7700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div data-testid="vb-frist-popup" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '86vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
              <b style={{ fontSize: 17 }}>📦 {T('fristPopupTitel')}</b>
              <div style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>{T('fristPopupText', { n: frist.abgeholt.length })}</div>
              <div style={{ fontSize: 13, color: f.sub, lineHeight: 1.6, maxHeight: 180, overflowY: 'auto', marginTop: 8, padding: '6px 10px', borderRadius: 8, background: f.zeile }}>
                {frist.abgeholt.map((z) => <div key={z.code}>{code(z.kurz)} · {z.name}{z.fach ? ` · ${z.fach}` : ''}</div>)}
              </div>
              <button type="button" data-testid="vb-frist-popup-ja" disabled={uebernimmt} onClick={fristUebernehmen} style={{ ...knopf(true), opacity: uebernimmt ? 0.6 : 1 }}>{uebernimmt ? '⏳' : `${T('uebernehmen')} (${frist.abgeholt.length})`}</button>
              <button type="button" data-testid="vb-frist-popup-nein" disabled={uebernimmt} onClick={() => setFristPopupZu(true)} style={knopf(false)}>{T('nichtJetzt')}</button>
            </div>
          </div>
        )}

        {anmeldenZeigen && (
          <div>
            {kopf(T('anmelden'))}
            <input data-testid="vb-email" type="email" autoComplete="off" placeholder={T('email')} value={email} onChange={(e) => setEmail(e.target.value)} style={feld} />
            <input data-testid="vb-passwort" type="password" autoComplete="off" placeholder={T('passwort')} value={passwort} onChange={(e) => setPasswort(e.target.value)} style={feld} />
            {hinweis(T('loginHinweis'))}
            <button type="button" data-testid="vb-login" disabled={meldetAn || !email.trim() || !passwort} onClick={anmelden} style={{ ...knopf(true), opacity: meldetAn || !email.trim() || !passwort ? 0.6 : 1 }}>
              {meldetAn ? T('meldetAn') : T('uebertragen')}
            </button>
          </div>
        )}
        {loginMeldung && <div data-testid="vb-login-meldung" style={{ marginTop: 8, fontSize: 14, color: loginMeldung.ok ? '#2e7d32' : '#c62828' }}>{loginMeldung.text}</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, fontSize: 15, fontWeight: 600, userSelect: 'none' }}>
          <input data-testid="vb-schalter" type="checkbox" checked={an}
            onChange={(e) => (e.target.checked || (!annahmeAn && !ausgabeAn) ? schalten(e.target.checked) : setLiveAusFrage('anzeige'))}
            style={{ width: 20, height: 20, flexShrink: 0, cursor: 'pointer' }} />
          <span>{T('schalter')}</span>
        </div>
        {hinweis(T('schalterHinweis'))}

        {/* LIVE buchen: getrennt vom Lesen, je Vorgang ein eigener Schalter, Ein-/Ausschalten nur nach Rückfrage */}
        <div data-testid="vb-live" style={{ marginTop: 16, padding: 12, borderRadius: 10, border: '2px solid #c62828' }}>
          <b style={{ fontSize: 15, color: '#c62828' }}>🔴 {T('liveTitel')}</b>
          <div style={{ fontSize: 13, color: f.text, marginTop: 4, lineHeight: 1.5 }}>{T('liveHinweis')}</div>
          {[['annahme', annahmeAn, 'schalterAnnahme'], ['ausgabe', ausgabeAn, 'schalterAusgabe']].map(([art, wert, text]) => (
            <div key={art} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, fontSize: 14, fontWeight: 600, opacity: an ? 1 : 0.5, userSelect: 'none' }}>
              <input data-testid={`vb-schalter-${art}`} type="checkbox" disabled={!an} checked={an && wert}
                onChange={(e) => (e.target.checked ? setLiveFrage(art) : setLiveAusFrage(art))} style={{ width: 20, height: 20, flexShrink: 0, cursor: an ? 'pointer' : 'default' }} />
              <span>{T(text)}{an && wert ? <span style={{ color: '#c62828' }}> · LIVE</span> : null}</span>
            </div>
          ))}
        </div>

        {/* Rückfrage vor dem Einschalten: das ist kein Probelauf */}
        {liveFrage && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div data-testid="vb-live-frage" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 440, padding: 18, border: '2px solid #c62828', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
              <b style={{ fontSize: 17, color: '#c62828' }}>🔴 {T('liveFrageTitel')}</b>
              <div style={{ fontSize: 14, marginTop: 10, lineHeight: 1.55 }}>{T(liveFrage === 'annahme' ? 'liveFrageAnnahme' : 'liveFrageAusgabe')}</div>
              <div style={{ fontSize: 13, color: f.sub, marginTop: 8, lineHeight: 1.5 }}>{T('liveFrageZusatz')}</div>
              <button type="button" data-testid="vb-live-ja" onClick={() => { liveSchalten(liveFrage, true); setLiveFrage(null); }} style={{ ...knopf(true), background: '#c62828' }}>{T('liveJa')}</button>
              <button type="button" data-testid="vb-live-nein" onClick={() => setLiveFrage(null)} style={knopf(false)}>{T('abbrechen')}</button>
            </div>
          </div>
        )}

        {/* Rückfrage vor dem Ausschalten: verhindert versehentliches Abwählen beim Scrollen */}
        {liveAusFrage && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div data-testid="vb-live-aus-frage" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 440, padding: 18, border: '2px solid #e65100', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
              <b style={{ fontSize: 17, color: '#e65100' }}>⚠️ {T('liveAusFrageTitel')}</b>
              <div style={{ fontSize: 14, marginTop: 10, lineHeight: 1.55 }}>{T(liveAusFrage === 'anzeige' ? 'liveAusFrageAnzeige' : liveAusFrage === 'annahme' ? 'liveAusFrageAnnahme' : 'liveAusFrageAusgabe')}</div>
              <button type="button" data-testid="vb-live-aus-nein" onClick={() => setLiveAusFrage(null)} style={knopf(true)}>{T('angelassen')}</button>
              <button type="button" data-testid="vb-live-aus-ja" onClick={() => { if (liveAusFrage === 'anzeige') schalten(false); else liveSchalten(liveAusFrage, false); setLiveAusFrage(null); }} style={{ ...knopf(false), color: '#c62828', borderColor: '#c62828' }}>{T('liveAusJa')}</button>
            </div>
          </div>
        )}

        {kopf(T('abgleich'))}
        {hinweis(T('abgleichHinweis'))}
        {abgleich && abgleich.laeuft
          ? <div data-testid="vb-abgleich-laeuft" style={{ marginTop: 10, fontSize: 14 }}>⏳ {T('abgleichLaeuft')}</div>
          : <button type="button" data-testid="vb-abgleich" onClick={starteAbgleich} style={knopf(false)}>{T('abgleichStart')}</button>}
        {abgleich && !abgleich.laeuft && abgleich.ok === false && <div style={{ marginTop: 8, fontSize: 14, color: '#c62828' }}>⚠️ {abgleich.message}</div>}
        {abgleich && abgleich.done && abgleich.ok && (
          <div data-testid="vb-abgleich-ergebnis" style={{ marginTop: 10, padding: 12, borderRadius: 8, background: f.zeile }}>
            <div style={{ fontSize: 14 }}>✅ {abgleich.gleich} {T('abGleich')} · Vinted {abgleich.vintedGesamt} · PaketPilot {abgleich.paketpilotGesamt}</div>
            {/* Abweichungen einzeln bearbeiten: hinzufügen (Name/Fach anpassbar), austragen, Namen übernehmen */}
            {aktionen(T('abNurVinted'), 'add', abgleich.nurVinted, (p) => (<>
              <input data-testid={`vb-name-${p.code}`} value={nameFuer[p.code] ?? p.name} onChange={(e) => setNameFuer((n) => ({ ...n, [p.code]: e.target.value }))} style={{ ...miniFeld, flex: '1 1 140px' }} />
              <input data-testid={`vb-fach-${p.code}`} placeholder={T('fachKurz')} value={fachFuer[p.code] || ''} onChange={(e) => setFachFuer((n) => ({ ...n, [p.code]: e.target.value }))} style={{ ...miniFeld, width: 64 }} />
            </>), T('hinzu'), hinzufuegen)}
            {aktionen(T('abNurPP'), 'aus', abgleich.nurPaketPilot, (p) => <span style={{ flex: '1 1 140px' }}>{p.name}</span>, T('austragen'), austragen)}
            {aktionen(T('abNameAnders'), 'name', abgleich.nameAnders, (p) => <span style={{ flex: '1 1 140px' }}>{p.paketpilot} → <b>{p.vinted}</b></span>, T('nameNehmen'), nameNehmen)}
            {liste(T('abNameFehlt'), abgleich.nameFehlt, (p) => `${code(p.code)}  ${p.name}`)}
            {abgleich.nameFehlt && abgleich.nameFehlt.length > 0 && namenGesetzt === null && (
              <button type="button" data-testid="vb-namen" onClick={namenSetzen} style={knopf(true)}>{T('abNamenSetzen')} ({abgleich.nameFehlt.length})</button>
            )}
            {namenGesetzt !== null && <div style={{ marginTop: 8, fontSize: 14, color: '#2e7d32' }}>✓ {namenGesetzt}</div>}
            {uebernommen !== null && <div data-testid="vb-uebernommen" style={{ marginTop: 8, fontSize: 14, color: '#2e7d32' }}>✓ {T('uebernommen', { n: uebernommen })}</div>}
            {abgeholt.alt.length > 0 && popupZu && uebernommen === null && (
              <button type="button" data-testid="vb-popup-wieder" onClick={() => setPopupZu(false)} style={knopf(false)}>{T('popupTitel')} ({abgeholt.alt.length})</button>
            )}
          </div>
        )}

        {/* Rückfrage: in Vinted ausgegeben → in PaketPilot übernehmen? (nie von selbst) */}
        {abgeholt.alt.length > 0 && !popupZu && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 7700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div data-testid="vb-popup" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '86vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
              <b style={{ fontSize: 17 }}>📦 {T('popupTitel')}</b>
              <div style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>{T('popupText', { n: abgeholt.alt.length })}</div>
              <div style={{ fontSize: 13, color: f.sub, lineHeight: 1.6, maxHeight: 180, overflowY: 'auto', marginTop: 8, padding: '6px 10px', borderRadius: 8, background: f.zeile }}>
                {abgeholt.alt.map((p) => <div key={p.id}>{code(vintedCode(p))} · {p.name}{p.regalplatz ? ` · ${p.regalplatz}` : ''}</div>)}
              </div>
              {abgeholt.frisch > 0 && <div style={{ fontSize: 13, color: f.sub, marginTop: 8, lineHeight: 1.5 }}>{T('popupFrisch', { m: abgeholt.frisch })}</div>}
              <button type="button" data-testid="vb-popup-ja" disabled={uebernimmt} onClick={uebernehmen} style={{ ...knopf(true), opacity: uebernimmt ? 0.6 : 1 }}>{uebernimmt ? '⏳' : `${T('uebernehmen')} (${abgeholt.alt.length})`}</button>
              <button type="button" data-testid="vb-popup-nein" disabled={uebernimmt} onClick={() => setPopupZu(true)} style={knopf(false)}>{T('nichtJetzt')}</button>
            </div>
          </div>
        )}

        {/* Übungs-Ausgabe (Testpaket von Vinted, PIN 000000) und Abmelden */}
        {angemeldet && (
          <div>
            {kopf(T('uebung'), <button type="button" data-testid="vb-uebung" disabled={!!(uebung && uebung.laeuft)} onClick={uebungTesten} style={{ ...klein, opacity: uebung && uebung.laeuft ? 0.6 : 1 }}>{uebung && uebung.laeuft ? T('uebungLaeuft') : T('uebungStart')}</button>)}
            {hinweis(T('uebungHinweis'))}
            {uebung && !uebung.laeuft && (
              <div data-testid="vb-uebung-ergebnis" style={{ marginTop: 6, fontSize: 14, color: uebung.ok ? '#2e7d32' : '#c62828' }}>
                {uebung.ok ? `✓ ${T('uebungOk', { s: ((uebung.ms || 0) / 1000).toFixed(1) })}` : `✗ ${[uebung.reason, uebung.message].filter(Boolean).join(' — ')}`}
              </div>
            )}
            <button type="button" data-testid="vb-logout" onClick={abmelden} style={knopf(false)}>{T('abmelden')}</button>
          </div>
        )}

        {kopf(T('protokoll'), <button type="button" data-testid="vb-log-refresh" onClick={ladeLog} style={klein}>{T('neuLaden')}</button>)}
        <div data-testid="vb-log">
          {log && log.length === 0 && hinweis(T('protokollLeer'))}
          {(log || []).map((e) => (
            <div key={e.id} onClick={() => setOffen(offen === e.id ? null : e.id)} style={{ padding: '8px 10px', borderRadius: 8, background: f.zeile, marginTop: 6, fontSize: 13, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span><b style={{ color: e.ok ? '#2e7d32' : '#c62828' }}>{e.ok ? '✓' : '✗'}</b> {T(`op_${String(e.op || '').replace(/^v2_/, '')}`)}{e.code && e.code !== 'PIN' && e.code !== 'QR' ? ` · ${code(String(e.code).slice(-6))}` : ''}</span>
                <span style={{ color: f.sub, whiteSpace: 'nowrap' }}>{zeit(e.created_at)}{e.ms ? ` · ${(e.ms / 1000).toFixed(1)} s` : ''}</span>
              </div>
              {(e.reason || e.message) && <div style={{ color: e.ok ? f.sub : '#c62828', marginTop: 2 }}>{[e.reason, e.message].filter(Boolean).join(' — ')}</div>}
              {offen === e.id && e.detail && <pre style={{ margin: '6px 0 0', fontSize: 11, color: f.sub, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(e.detail, null, 1)}</pre>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
